import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { openModal, closeModal } from 'store/redux-slices/modal'
import { MODALS } from 'constants/modals'
import AuthModal from 'components/AuthModal'
import { AUTH_MODAL_TABS } from 'constants/general'

const VALID_TABS = new Set([
  AUTH_MODAL_TABS.LOGIN,
  AUTH_MODAL_TABS.SIGNUP,
  AUTH_MODAL_TABS.FORGOT_PASSWORD
])

const AuthModalUrlHandler = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const generalConfig = useSelector(state => state?.uiConfig?.general)
  const isUserBlock = useSelector((state) => state.user?.isUserBlock)
  const isLoggedIn = useSelector((state) => state.login?.isLoggedIn)

  // isAuthOpen is read inside the effect but intentionally NOT added to the dep array.
  // The URL (location.search) is the single reactive driver for this effect.
  // Each render creates a fresh effect function with the latest isAuthOpen value,
  // so when the effect runs (triggered by URL or login change), it always reads
  // the correct value — no stale closure, no ref needed.
  const isAuthOpen = useSelector((state) =>
    Boolean(state.modal.modals?.some(m => m?.name === MODALS.AUTH && m?.isOpen))
  )

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('auth-modal')
    const isValid = tab && VALID_TABS.has(tab)

    // If the country is blocked, immediately clear auth-modal from URL and close modal
    if (isUserBlock) {
      if (tab) {
        params.delete('auth-modal')
        const newSearch = params.toString()
        navigate(
          { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
          { replace: true }
        )
      }
      if (isAuthOpen) {
        dispatch(closeModal(MODALS.AUTH))
      }
      return
    }

    // If user is logged in and auth-modal is in URL, remove it immediately
    if (isLoggedIn && tab) {
      let redirectPath = location.pathname
      if (window.locationPath) {
        redirectPath = window.locationPath
        window.locationPath = null
      }

      params.delete('auth-modal')
      const newSearch = params.toString()
      navigate(
        { pathname: redirectPath, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      )
      if (isAuthOpen) {
        dispatch(closeModal(MODALS.AUTH))
      }
      return
    }

    if (!generalConfig?.modalBasedAuth) {
      if (isAuthOpen) {
        dispatch(closeModal(MODALS.AUTH))
      }
      return
    }

    if (isValid && !isAuthOpen) {
      dispatch(openModal({
        name: MODALS.AUTH,
        body: (
          <AuthModal
            currentTab={tab}
          />
        ),
        modalProps: {
          className: 'auth-modal-header'
        },
        onClose: () => dispatch(closeModal(MODALS.AUTH))
      }))
    }

    if (!isValid && isAuthOpen) {
      dispatch(closeModal(MODALS.AUTH))
    }
  }, [location.search, location.pathname, dispatch, navigate, generalConfig?.modalBasedAuth, isUserBlock, isLoggedIn])
  return null
}

export default AuthModalUrlHandler
